import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { saveAccountWallet, friendlyApiError } from "@/lib/api";
import { isValidAddress } from "@/lib/wallet";
import { useUser } from "@/context/UserContext";
import { toast } from "sonner";
import { Loader2, Pencil } from "lucide-react";

/**
 * Post-login wallet step. Shown automatically after X sign-in when the
 * account has no wallet yet: paste an address to attach it to the account.
 */
export default function WalletOnboardingModal({ open, onClose }) {
  const { refresh } = useUser();
  const [pasted, setPasted] = useState("");
  const [busy, setBusy] = useState(false);

  const done = async (message) => {
    setPasted("");
    setBusy(false);
    try {
      await refresh();
    } catch {
      toast.error("Saved, but the profile did not reload.");
      return;
    }
    toast.success(message);
    onClose();
  };

  const failToast = (err, fallback) => {
    const code = err.response?.data?.detail;
    if (code === "WALLET_ALREADY_LINKED") {
      toast.error("That wallet already belongs to another Osteon account.");
    } else if (err.response?.status === 400) {
      toast.error("That is not a valid EVM wallet address.");
    } else {
      toast.error(friendlyApiError(err, fallback));
    }
  };

  const onSave = async (e) => {
    e?.preventDefault();
    const candidate = pasted.trim();
    if (!isValidAddress(candidate)) {
      toast.error("Enter a valid 0x wallet address (40 hex characters).");
      return;
    }
    setBusy(true);
    try {
      await saveAccountWallet(candidate);
      await done("Wallet saved");
    } catch (err) {
      failToast(err, "Unable to save this wallet. Please try again.");
      setBusy(false);
    }
  };

  const handleClose = () => {
    setPasted("");
    setBusy(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent
        data-testid="wallet-onboarding-modal"
        className="bg-[#0d1017] border border-[#1b2234] text-slate-100 sm:max-w-md p-0 clip-terminal overflow-hidden"
      >
        <div className="p-8 pt-10">
          <DialogHeader>
            <div className="font-mono-cyber text-[10px] uppercase tracking-[0.35em] text-[#00ff66] mb-2">
              // Step 2 of 2 · Wallet Address
            </div>
            <DialogTitle className="font-editorial text-3xl font-normal text-white">
              Add your <span className="italic text-[#00ff66]">wallet</span>.
            </DialogTitle>
            <p className="text-sm text-slate-400 mt-3 font-editorial italic leading-relaxed">
              Paste your wallet address below. It will be saved on your account.
            </p>
          </DialogHeader>

          <div className="mt-8 space-y-5">
            <form onSubmit={onSave} className="space-y-4">
              <div>
                <Label className="font-mono-cyber text-[10px] uppercase tracking-[0.3em] text-[#00f0ff]">
                  Wallet Address
                </Label>
                <Input
                  data-testid="wallet-onboarding-paste-input"
                  value={pasted}
                  onChange={(e) => setPasted(e.target.value.trim())}
                  placeholder="0x…"
                  spellCheck={false}
                  autoComplete="off"
                  className="mt-2 bg-[#07080c] border-[#1b2234] rounded-none h-12 font-mono-cyber text-sm"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                data-testid="wallet-onboarding-save"
                className="w-full rounded-none clip-notch h-12 bg-[#00ff66] hover:bg-[#00ff66]/90 text-black font-mono-cyber uppercase tracking-widest text-xs"
              >
                {busy ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <><Pencil className="w-4 h-4 mr-2" /> Save Wallet</>
                )}
              </Button>
            </form>

            <button
              type="button"
              onClick={handleClose}
              data-testid="wallet-onboarding-skip"
              className="w-full text-center font-mono-cyber text-[11px] uppercase tracking-widest text-slate-500 hover:text-slate-300"
            >
              Skip for now
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
